"use client"

import React from "react"
import {
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from "react-hook-form"

import {
  FormControl,
  FormItem,
  FormMessage
} from "@/components/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

type SelectPickerProps<
  TFormValues extends FieldValues,
  TFieldName extends Path<TFormValues>,
> = {
  field: ControllerRenderProps<TFormValues, TFieldName>
  children?: React.ReactNode
  options: { value: TFormValues[TFieldName]; label: string; icon: string }[]
  placeholder?: React.ReactNode
  icon?: string
}

export default function SelectPicker<
  TFormValues extends FieldValues,
  TFieldName extends Path<TFormValues>,
>(props: SelectPickerProps<TFormValues, TFieldName>) {
  const { field, options, children, icon, placeholder } = props

  return (
    <FormItem className="space-y-3">
      {children}
      <FormControl>
        <Select onValueChange={field.onChange} defaultValue={field.value}>
          <SelectTrigger className="bg-muted">
            <SelectValue aria-label={field.value} placeholder={placeholder}>
              {icon}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {options.map(({ value, label, icon }) => (
              <SelectItem key={value} value={value}>
                <span className="mr-2">{icon}</span>
                <span className="capitalize">{label}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}
